import {
  color,
  Divider,
  IconFolder,
  IconLogoUnsplash,
  IconRobot,
  IconUpload,
  ListItem,
  rem,
  spacing,
  styled,
  Tag,
  TextElement,
  useTranslation,
} from '@lessonup/ui-components';
import React, { useState } from 'react';
import type { Key } from 'react-aria-components';
import { Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { useFeatureFlags } from '../../../../../utils/featureFlags/useFeatureFlags';
import { showUploadsModal } from '../../../../Uploads/UploadsModalFeature';
import { useEditorContextSidebar } from '../../EditorContextSidebar/hooks/useEditorContextSidebar';
import { HandleAddImage, ImagePickerModelSharedProps } from './ImagePickerModal.types';
import { AiImageTab } from './components/AiImageTab';
import { StockPhotoTab } from './components/StockPhotoTab';
import { UploadImageTab } from './components/UploadImageTab';
import { UploadsTab } from './components/UploadsTab';

interface ImagePickerModalContentProps extends ImagePickerModelSharedProps {
  handleAddImage: HandleAddImage;
  closeModal: () => void;
}

export const ImagePickerModalContent: React.FC<ImagePickerModalContentProps> = ({
  handleAddImage,
  closeModal,
  t,
  acceptMultipleFiles,
  initialQuery,
  trackingContext,
}) => {
  const [selectedTab, setSelectedTab] = useState<Key>('upload');
  const [userSearchedForStockPhotos, setUserSearchedForStockPhotos] = useState<boolean>(false);
  const sidebar = useEditorContextSidebar();
  const { isFeatureEnabled } = useFeatureFlags();
  const { t: tCommon } = useTranslation('common');

  const handleUploadFromModal = async () => {
    const result = await showUploadsModal({ fileTypeFilter: ['ImageUpload', 'DocumentUpload'] });
    const urls = result?.urls.filter(Boolean) || [];

    if (urls.length) {
      return handleAddImage({ urls, source: 'upload' });
    }
  };

  const handleGenerateWithAI = () => {
    closeModal();
    sidebar.callAction('aiTools', 'aiToolsGenerateImage');
  };

  return (
    <Wrapper>
      <StyledTabs
        orientation="vertical"
        selectedKey={selectedTab}
        onSelectionChange={(key) => setSelectedTab(key as string)}
      >
        <TabListContainer>
          <StyledTabList aria-label={t('imagePicker.tabListAriaLabel')}>
            <Tab id="your-images" isDisabled>
              <StyledLabel textStyle="label" size="small">
                {t('imagePicker.myImagesTabLabel')}
              </StyledLabel>
            </Tab>

            <Tab id="upload">
              <TabListItem label={t('imagePicker.uploadTab')} active={selectedTab === 'upload'} icon={<IconUpload />} />
            </Tab>
            <Tab id="my-uploads" onPress={handleUploadFromModal}>
              <TabListItem
                label={t('imagePicker.uploadsTab')}
                active={selectedTab === 'my-uploads'}
                icon={<IconFolder />}
              />
            </Tab>

            <OtherTab id="other" isDisabled>
              <StyledLabel textStyle="label" size="small">
                {t('imagePicker.otherImagesTabLabel')}
              </StyledLabel>
            </OtherTab>

            <Tab id="stock-photos">
              <TabListItem
                label={t('imagePicker.stockTab')}
                active={selectedTab === 'stock-photos'}
                icon={<IconLogoUnsplash />}
                endSlot={
                  <Tag size="small" colorMode="tertiary">
                    {tCommon('new')}
                  </Tag>
                }
              />
            </Tab>
            {isFeatureEnabled('imageGenerator') && (
              <Tab id="ai" onPress={handleGenerateWithAI}>
                <TabListItem
                  label={t('imagePicker.generateWithAiTab')}
                  active={selectedTab === 'ai'}
                  icon={<IconRobot />}
                />
              </Tab>
            )}
          </StyledTabList>
        </TabListContainer>

        <DividerWrapper>
          <Divider orientation="vertical" />
        </DividerWrapper>

        <StyledTabPanel id="upload">
          <UploadImageTab acceptMultipleFiles={acceptMultipleFiles} handleAddImage={handleAddImage} t={t} />
        </StyledTabPanel>
        <StyledTabPanel id="my-uploads">
          <UploadsTab handleUploadFromModal={handleUploadFromModal} t={t} />
        </StyledTabPanel>
        <StyledTabPanel id="stock-photos" shouldForceMount={userSearchedForStockPhotos}>
          <StockPhotoTab
            handleAddImage={handleAddImage}
            initialQuery={initialQuery}
            setUserSearchedForStockPhotos={setUserSearchedForStockPhotos}
            trackingContext={trackingContext}
          />
        </StyledTabPanel>
        {isFeatureEnabled('imageGenerator') && (
          <StyledTabPanel id="ai">
            <AiImageTab handleGenerateWithAI={handleGenerateWithAI} t={t} />
          </StyledTabPanel>
        )}
      </StyledTabs>
    </Wrapper>
  );
};

const TabListItem: React.FC<{
  label: string;
  active: boolean;
  icon: React.JSX.Element;
  endSlot?: React.JSX.Element;
}> = ({ label, active, icon, endSlot }) => {
  return (
    <StyledTabListItem
      text={label}
      icon={icon}
      type={active ? 'active' : 'default'}
      endSlot={endSlot}
      size="medium"
      indentation={true}
      roundedCorners
      // @ts-expect-error - as is not typed in the ListItem component, but it works
      as="div"
    />
  );
};

const Wrapper = styled.div`
  width: ${rem('768px')};
  height: ${rem('512px')};
`;

const StyledTabs = styled(Tabs)`
  display: flex;
  flex-direction: row;
  height: 100%;
`;

const StyledTabList = styled(TabList)`
  width: ${rem('272px')};
  padding-right: ${spacing.size8};
  display: flex;
  flex-direction: column;
  gap: ${spacing.size4};
  padding: ${spacing.size16};
`;

const TabListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.size8};
`;

const StyledTabPanel = styled(TabPanel)`
  flex: 1;
  padding: ${spacing.size16};
  display: flex;
  align-items: center;
  justify-content: center;

  > * {
    width: 100%;
  }

  &[data-inert='true'] {
    display: none;
  }
`;

const DividerWrapper = styled.div`
  width: auto;
`;

const OtherTab = styled(Tab)`
  margin-top: ${spacing.size12};
`;

const StyledTabListItem = styled(ListItem)`
  width: 100%;
  cursor: pointer;
`;

const StyledLabel = styled(TextElement)`
  color: ${color.neutralNew.white.onSubjacent};
  margin-left: ${spacing.size4};
`;
